import {
    User,
    UserPrivateInfo,
    UserPrivatePreferences,
    USER_PRIVATE_FINANCE_DOCUMENT,
    USER_PRIVATE_INFO_DOCUMENT,
    USER_PRIVATE_LOGIN_DOCUMENT,
    USER_PRIVATE_NOTIFICATIONS_DOCUMENT,
    USER_PRIVATE_PREFERENCES_DOCUMENT,
    USER_PRIVATE_PRIVACY_DOCUMENT,
    USER_PRIVATE_REFERRALS_DOCUMENT,
    ReviewFieldFrom, ReviewFieldTo,
    EUserRole,
    USER_PROTECTED_INFO_DOCUMENT
} from "../../types/db";
import { auth, db } from "../firebase";
import { UserService } from "../../types/services/user/UserService";
import { InfoTypeDocId as PrivateInfoTypeDocId, InfoTypeMap as PrivateInfoTypeMap, UserPrivateInfosService } from "../../types/services/user/UserPrivateInfosService";
import { InfoTypeDocId as ProtectedInfoTypeDocId, InfoTypeMap as ProtectedInfoTypeMap, UserProtectedInfosService } from "../../types/services/user/UserProtectedInfosService";
import { applyVariablesToQuery, QueryVariables } from "../db";
import { ListingFEService } from "../listing";

/** The type of information of the user can be updated from the Front-End */
type TUserUpdate = Pick<User, 'display_name' | 'avatar_url' | 'description' | 'from' | 'languages'>

/**
 * The User Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
export class UserFEService<TUpdate = TUserUpdate> extends UserService {
    constructor() {
        super(db);
    }

    public protectedInfoService(userId: string): UserProtectedInfosService {
        return new UserProtectedInfosFEService(userId)
    }

    public privateInfoService(userId: string): UserPrivateInfosService {
        return new UserPrivateInfosFEService(userId)
    }

    /**
     * New User cannot be created from Front-End
     * @throws if used
     */
    create(data: never, id?: string): Promise<string> {
        return Promise.reject('Error: UNAUTHORIZED - Trying to create a new user from the Front-End')
    }

    /**
     * Update the public user information
     * @param userId the user id
     * @param data the user info to update (can only update certain value from the front-end/by the user)
     * @throws if current user is not the provided id
     */
    update(userId: string, data: Partial<TUpdate>): Promise<void> {
        if (auth.currentUser?.uid !== userId) {
            return Promise.reject('Trying to update a user data who is not itself')
        }
        return super.update(userId, data);
    }

    /**
     * Merge data into the public user information
     * @param userId the user id
     * @param data the user info to merge into the existing data (can only update certain value from the front-end/by the user)
     * @throws if current user is not the provided id
     * @see update for normal usage for data update
     */
    merge(userId: string, data: Partial<TUpdate>): Promise<void> {
        if (auth.currentUser?.uid !== userId) {
            return Promise.reject('Trying to merge info into a user data who is not itself')
        }
        return super.merge(userId, data);
    }

    /**
     * User cannot be delete from Front-End
     * @param userId
     * @throws if used even if current user
     */
    delete(userId: string): Promise<void> {
        if (auth.currentUser?.uid !== userId) {
            return Promise.reject('Trying to delete a user data who is not itself')
        }
        return Promise.reject('Error: UNAUTHORIZED - Trying to delete a user from the Front-End')
    }

    private throwError() { return Promise.reject('Error: UNAUTHORIZED - Trying to touch user public infos from the Front-End') }
    batchCreate<T>(entities: { id?: T; data: never }[]): Promise<T[]> {
        return this.throwError();
    }

    batchUpdate(updates: { id: string; data: Partial<TUpdate>; }[]): Promise<void> {
        if (updates.some(val => val.id !== auth.currentUser?.uid)) {
            //some of the batch update id isn't from current user
            return Promise.reject('Trying to batch update a user data who is not itself')
        }
        return super.batchUpdate(updates);
    }

    batchMerge(updates: { id: string; data: Partial<TUpdate>; }[]): Promise<void> {
        if (updates.some(val => val.id !== auth.currentUser?.uid)) {
            //some of the batch update id isn't from current user
            return Promise.reject('Trying to batch merge a user data who is not itself')
        }
        return super.batchMerge(updates);
    }

    batchDelete<T>(ids: T[]): Promise<void> {
        return this.throwError();
    }
}

/**
 * The User Private infos Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
class UserPrivateInfosFEService extends UserPrivateInfosService {
    constructor(protected userId: string) {
        super(db, userId);
    }
    /**
     * User private info can only be retrieved by its owner
     * @throws if current user is not the provided id
     */
    get<T extends PrivateInfoTypeDocId>(id: T): Promise<PrivateInfoTypeMap[T] | null> {
        if (auth.currentUser?.uid !== this.userId) {
            return Promise.reject('Trying to get a user private data who is not itself')
        }
        return super.get(id);
    }
    /**
     * New User private info cannot be created from Front-End 
     * (the creation is done by cloud function at user creation)
     * @throws if used
     */
    create<T>(data: never, id: never): Promise<T> {
        return Promise.reject('Error: UNAUTHORIZED - Trying to create a new user private infos from the Front-End')
    }

    /**
     * Update the private user information
     * @param userId the user id
     * @param data the user info to update (can only update certain value from the front-end/by the user)
     * @throws if current user is not the provided id
     */
    update<T extends PrivateInfoTypeDocId>(id: T, data: Partial<PrivateInfoTypeMap[T]>): Promise<void> {
        if (auth.currentUser?.uid !== this.userId) {
            return Promise.reject('Trying to update a user data who is not itself')
        }
        return super.update(id, data);
    }

    /**
     * Merge data into the private user information
     * @param userId the user id
     * @param data the user info to merge into the existing data (can only update certain value from the front-end/by the user)
     * @throws if current user is not the provided id
     * @see update for normal usage for data update
     */
    merge<T extends PrivateInfoTypeDocId>(id: T, data: Partial<PrivateInfoTypeMap[T]>): Promise<void> {
        if (auth.currentUser?.uid !== this.userId) {
            return Promise.reject('Trying to merge info into a user data who is not itself')
        }
        return super.merge(id, data);
    }

    /**
     * New User private info cannot be deleted from Front-End
     * (the deletion, if any, is done by cloud function at user creation)
     * @throws if used
     */
    delete<T>(id: T): Promise<void> {
        return Promise.reject('Error: UNAUTHORIZED - Trying to delete a new user private infos from the Front-End')
    }

    private throwError() { return Promise.reject('Error: UNAUTHORIZED - Trying to touch user private infos from the Front-End') }
    batchCreate<T>(entities: { id?: T; data: never }[]): Promise<T[]> {
        return this.throwError();
    }

    batchUpdate<T extends PrivateInfoTypeDocId>(updates: { id: T; data: Partial<PrivateInfoTypeMap[T]>; }[]): Promise<void> {
        if (auth.currentUser?.uid !== this.userId) {
            //some of the batch update id isn't from current user
            return Promise.reject('Trying to batch update a user data who is not itself')
        }
        return super.batchUpdate(updates);
    }

    batchMerge<T extends PrivateInfoTypeDocId>(updates: { id: T; data: Partial<PrivateInfoTypeMap[T]>; }[]): Promise<void> {
        if (auth.currentUser?.uid !== this.userId) {
            //some of the batch update id isn't from current user
            return Promise.reject('Trying to batch merge a user data who is not itself')
        }
        return super.batchMerge(updates);
    }

    batchDelete<T>(ids: T[]): Promise<void> {
        return this.throwError();
    }
}

/**
 * The User Protected infos Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
class UserProtectedInfosFEService extends UserProtectedInfosService {
    constructor(protected userId: string) {
        super(db, userId);
    }
    /**
     * User protected info can only be retrieved by its owner and authorized personnel
     * @throws if current user is not the provided id
     */
    get<T extends ProtectedInfoTypeDocId>(id: T): Promise<ProtectedInfoTypeMap[T] | null> {
        if (auth.currentUser?.uid !== this.userId || false) {
            //TODO do we want to check if user allowed to get protected info on FrontEnd or BE security rule is enough?
            return Promise.reject('Trying to get a user private data who is not itself')
        }
        return super.get(id);
    }
    /**
     * New User protected info cannot be created from Front-End
     * (the creation is done by cloud function at user creation)
     * @throws if used
     */
    create<T>(data: never, id: never): Promise<T> {
        return Promise.reject('Error: UNAUTHORIZED - Trying to create a new user protected infos from the Front-End')
    }
    /**
     * User protected info cannot be updated from Front-End
     * (all updates are done by cloud function)
     * @throws if used
     */
    update<T>(id: T, data: never): Promise<void> {
        //the user protected info cannot be updated manually by user (done by the cloud)
        return Promise.reject('Error: UNAUTHORIZED - Trying to update a new user protected infos from the Front-End')
    }
    /**
     * User protected info cannot be updated from Front-End
     * (all updates are done by cloud function)
     * @throws if used
     */
    merge<T>(id: T, data: never): Promise<void> {
        //same as update
        return Promise.reject('Error: UNAUTHORIZED - Trying to merge a new user protected infos from the Front-End')
    }
    /**
     * User protected info cannot be deleted from Front-End
     * (the deletion, if any, is done by cloud function at user creation)
     * @throws if used
     */
    delete<T>(id: T): Promise<void> {
        return Promise.reject('Error: UNAUTHORIZED - Trying to delete a new user protected infos from the Front-End')
    }

    private throwError() { return Promise.reject('Error: UNAUTHORIZED - Trying to touch user protected infos from the Front-End') }
    batchCreate<T>(entities: { id?: T; data: never }[]): Promise<T[]> {
        return this.throwError();
    }
    batchUpdate<T>(updates: { id: T; data: never }[]): Promise<void> {
        return this.throwError();
    }
    batchMerge<T>(updates: { id: T; data: never }[]): Promise<void> {
        return this.throwError();
    }
    batchDelete<T>(ids: T[]): Promise<void> {
        return this.throwError();
    }
}

const userService = new UserFEService();

/***********************************************
 * USER PUBLIC
 ***********************************************/

/**
 * Get the protected user general information
 * @param userId  the user id
 * @returns The user protected information
 */
export const getUserPublicInfo = async (userId: string) => {
    return userService.get(userId);
}

/**
 * Update the public user information
 * @param userId the user id
 * @param info the user info to update (can only update certain value from the front-end/by the user)
 */
export const updateUserPublicInfo = (userId: string, info: Partial<TUserUpdate>) => {
    return userService.update(userId, info);
}

/***********************************************
 * USER PROTECTED
 ***********************************************/

/**
 * Get the protected user general information
 * @param userId  the user id
 * @returns The user protected information
 */
export const getUserProtectedInfo = async (userId: string) => {
    return userService.protectedInfoService(userId).get(USER_PROTECTED_INFO_DOCUMENT);
}

/***********************************************
 * USER PRIVATE
 ***********************************************/

/**
 * Get the private user general information
 * @param userId  the user id
 * @returns The user private information
 */
export const getUserPrivateInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_INFO_DOCUMENT);
}

/**
 * Update the private user information
 * @param userId the user id
 * @param info the user info to update (can only update certain value from the front-end/by the user)
 */
export const updateUserPrivateInfo = (userId: string, info: Partial<Pick<UserPrivateInfo, 'full_name' | 'email' | 'phone' | 'address' | 'emergency_contact'>>) => {
    return userService.privateInfoService(userId).merge(USER_PRIVATE_INFO_DOCUMENT, info);
}

/**
 * Get the private user login information
 * @param userId the user id
 * @returns The user login information
 */
export const getUserLoginInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_LOGIN_DOCUMENT)
}

/**
 * Get the private user finance information
 * @param userId  the user id
 * @returns The user finance information
 */
export const getUserFinanceInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_FINANCE_DOCUMENT)
}

/**
 * Get the private user notification information
 * @param userId  the user id
 * @returns The user notification information
 */
export const getUserNotificationInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_NOTIFICATIONS_DOCUMENT)
}

/**
 * Get the private user privacy information
 * @param userId  the user id
 * @returns The user privacy information
 */
export const getUserPrivacyInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_PRIVACY_DOCUMENT)
}

/**
 * Get the private user preferences information
 * @param userId  the user id
 * @returns The user preferences information
 */
export const getUserPreferencesInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_PREFERENCES_DOCUMENT)
}

/**
 * Update the private user information
 * @param userId the user id
 * @param info the user info to update
 */
export const updateUserPreferencesInfo = (userId: string, info: Partial<UserPrivatePreferences>) => {
    return userService.privateInfoService(userId).merge(USER_PRIVATE_PREFERENCES_DOCUMENT, info);
}

/**
 * Get the private user referrals information
 * @param userId  the user id
 * @returns The user referrals information
 */
export const getUserReferralsInfo = async (userId: string) => {
    return userService.privateInfoService(userId).get(USER_PRIVATE_REFERRALS_DOCUMENT)
}

/***********************************************
 * USER REVIEWS
 ***********************************************/

export type UserReviewQueryParams = {
    limit?: number;
    from: ReviewFieldFrom;
    to: ReviewFieldTo;
}

export const getUserReviews = async (userId: string, queryParams?: UserReviewQueryParams) => {
    return userService.reviewService(userId).list((collection) => {
        return applyVariablesToQuery(collection, queryParams ? {
            where: [
                { fieldPath: "from", opStr: '==', value: queryParams.from },
                { fieldPath: "to", opStr: '==', value: queryParams.to },
                ...(queryParams.to === EUserRole.HOST ? [{ fieldPath: "host.id", opStr: '==', value: queryParams.to }] : []),
                ...(queryParams.to === EUserRole.COHOST ? [{ fieldPath: "cohost.id", opStr: '==', value: queryParams.to }] : []),
                ...(queryParams.to === EUserRole.GUEST ? [{ fieldPath: "guest.id", opStr: '==', value: queryParams.to }] : []),
            ],
            limit: queryParams.limit
        } as QueryVariables : undefined);
    })
}


/***********************************************
 * USER LISTINGS
 ***********************************************/

/**
 * Get the User's listings (a one get shot, not on the fly update)
 * @param userId The user id
 * @param userRole The user role (to check for host's listing or cohost's listing)
 * @param root If we only want the root/parent listings or all of them
 * @returns the list of listings
 * @see useUserListings for listings with on the fly updates
 */
export const getUserListings = async (userId: string, userRole: EUserRole, root = true) => {
    if (userRole === EUserRole.GUEST) {
        //guests does not own/manage listings
        return [];
    }

    return new ListingFEService().list((collection) => {
        return applyVariablesToQuery(collection,
            {
                where: [
                    ...(userRole === EUserRole.HOST ? [{ fieldPath: "host.id", opStr: '==', value: userId }] : []),
                    ...(userRole === EUserRole.COHOST ? [{ fieldPath: "cohost.id", opStr: '==', value: userId }] : []),
                    ...(!!root ? [{ fieldPath: "parent_id", opStr: '==', value: null }] : [])
                ]
            } as QueryVariables
        )
    })
}